import { useState, useCallback } from "react";
import { ImageUpload, AutoTaggingResult, ClothingItem, SearchResultItem, VisualSearchResultsMap, ClothingItemSearchState } from "../types/unified-services";

const createInitialImageUpload = (): ImageUpload => ({
  file: null,
  base64: null,
  preview: null,
  isValid: false,
  error: undefined,
});

export interface VisualSearchState {
  image: ImageUpload;
  recognizedClothing: AutoTaggingResult | null;
  hoveredItem: ClothingItem | null;
  selectedItem: ClothingItem | null;
  searchResultsMap: VisualSearchResultsMap;
  searchParams: {
    country: string;
    count: number;
    page: number;
  };
  currentSearchResults: SearchResultItem[];
  currentPage: number;
  totalPages: number;
  isFirstServiceCalled: boolean;
  searchLoading: boolean;
  searchError: string | null;
}

export interface VisualSearchActions {
  setImage: (image: ImageUpload) => void;
  setRecognizedClothing: (clothing: AutoTaggingResult | null) => void;
  setHoveredItem: (item: ClothingItem | null) => void;
  setSelectedItem: (item: ClothingItem | null) => void;
  setSearchParams: (params: Partial<VisualSearchState["searchParams"]>) => void;
  setCurrentSearchResults: (results: SearchResultItem[]) => void;
  setCurrentPage: (page: number) => void;
  setTotalPages: (pages: number) => void;
  setIsFirstServiceCalled: (called: boolean) => void;
  setSearchLoading: (loading: boolean) => void;
  setSearchError: (error: string | null) => void;
  updateSearchResultsMap: (itemIndex: number, state: ClothingItemSearchState) => void;
  resetVisualSearch: () => void;
}

export const useVisualSearchState = (): VisualSearchState & VisualSearchActions => {
  const [image, setImage] = useState<ImageUpload>(createInitialImageUpload);
  const [recognizedClothing, setRecognizedClothing] = useState<AutoTaggingResult | null>(null);
  const [hoveredItem, setHoveredItem] = useState<ClothingItem | null>(null);
  const [selectedItem, setSelectedItem] = useState<ClothingItem | null>(null);
  const [searchResultsMap, setSearchResultsMap] = useState<VisualSearchResultsMap>({});
  const [searchParams, setSearchParamsState] = useState({
    country: "us",
    count: 3,
    page: 1,
  });
  const [currentSearchResults, setCurrentSearchResults] = useState<SearchResultItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isFirstServiceCalled, setIsFirstServiceCalled] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const setSearchParams = useCallback((params: Partial<VisualSearchState["searchParams"]>) => {
    setSearchParamsState((prev) => ({ ...prev, ...params }));
  }, []);

  const updateSearchResultsMap = useCallback((itemIndex: number, state: ClothingItemSearchState) => {
    setSearchResultsMap((prev) => ({
      ...prev,
      [itemIndex]: state,
    }));
  }, []);

  const resetVisualSearch = useCallback(() => {
    setImage(createInitialImageUpload);
    setRecognizedClothing(null);
    setHoveredItem(null);
    setSelectedItem(null);
    setSearchResultsMap({});
    setSearchParamsState({
      country: "us",
      count: 3,
      page: 1,
    });
    setCurrentSearchResults([]);
    setCurrentPage(1);
    setTotalPages(1);
    setIsFirstServiceCalled(false);
    setSearchLoading(false);
    setSearchError(null);
  }, []);

  return {
    image,
    recognizedClothing,
    hoveredItem,
    selectedItem,
    searchResultsMap,
    searchParams,
    currentSearchResults,
    currentPage,
    totalPages,
    isFirstServiceCalled,
    searchLoading,
    searchError,

    setImage,
    setRecognizedClothing,
    setHoveredItem,
    setSelectedItem,
    setSearchParams,
    setCurrentSearchResults,
    setCurrentPage,
    setTotalPages,
    setIsFirstServiceCalled,
    setSearchLoading,
    setSearchError,
    updateSearchResultsMap,
    resetVisualSearch,
  };
};
